import { UUID } from "crypto";
import { baseApi } from "./baseApi";
import {
    ContractualDoc,
    DocumentInfo,
    Dossier,
    InternalSignatories,
    PayloadDocument,
    PayloadSignUrl,
    PayloadUploadFile,
    SignedUrlFile,
} from "@interfaces/documents";
import { Response, ResponsePagination } from "@interfaces/response";

import DocumentsDataDummy from "../dataDummy/documents.json";
import DocumentsV2DataDummy from "../dataDummy/documentsV2.json";

const documentsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getDocumentsByApp: build.query<Dossier, { applicationId: UUID; type: "company" | "shareholders_owners" }>({
            query: ({ applicationId, type }) => ({
                url: `solicitudes/${applicationId}/bpm/documents`,
                params: {
                    q: `type:${type}`,
                },
            }),
            transformResponse: (response: Response<Dossier>) => {
                return response.data;
            },
        }),
        getFileDocuments: build.query<Response<Dossier>, { applicationId: UUID; params: string }>({
            query: ({ applicationId, params }) => ({
                url: `solicitudes/${applicationId}/bpm/documents?${params}`,
            }),
            // transformResponse: (response: DocumentsResponse) => {
            //   return response.data;
            // },
        }),
        postDocumentsZipLink: build.mutation<string, { applicationId: UUID; userId: UUID }>({
            query: ({ applicationId, userId }) => ({
                url: `documentos/download_zip_request`,
                method: "POST",
                body: {
                    loan_application_id: applicationId,
                    user_id: userId,
                },
            }),
        }),
        getDocuments: build.query<Array<DocumentInfo>, {}>({
            // query: (params) => ({
            //     url: `documentos`,
            //     params: params,
            // }),
            // transformResponse: (response: Response<Array<DocumentInfo>>) => {
            //     return response.data;
            // },

            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.loan_application_id === "3912e960-7795-434f-bc47-d277ff43937e") {
                    const params = new URLSearchParams(args);
                    let filtered: Array<any> = DocumentsDataDummy;
                    if (params.get("file_category_id")) {
                        filtered = filtered.filter((t) => params.get("file_category_id") === t.file_category_id);
                    }

                    return {
                        data: filtered,
                    };
                }
                const result = await baseQuery({
                    url: `/documentos`,
                    params: args,
                });

                return result.data;
            },
        }),
        getDocumentsV2: build.query<Array<DocumentInfo>, string>({
            // query: (params) => ({
            //     url: `documentos?${params}`,
            // }),
            // transformResponse: (response: Response<Array<DocumentInfo>>) => {
            //     return response.data;
            // },
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.includes("3912e960-7795-434f-bc47-d277ff43937e")) {
                    const params = new URLSearchParams(args);

                    let filtered: Array<any> = DocumentsV2DataDummy;
                    if (params.has("person_id")) {
                        filtered = filtered.filter((t) => t.client_details_id === params.get("person_id"));
                    }

                    if (params.has("client_details_id")) {
                        filtered = filtered.filter((t) => t.client_details_id === params.get("client_details_id"));
                    }

                    if (params.has("file_code")) {
                        filtered = filtered.filter((t) => params.getAll("file_code").includes(t.file_code));
                    }

                    if (params.has("file_category_id")) {
                        filtered = filtered.filter((t) =>
                            params.getAll("file_category_id").includes(t.file_category_id),
                        );
                    }

                    return {
                        data: filtered,
                    };
                }

                const result = await baseQuery({
                    url: `documentos?${args}`,
                });

                return result.data;
            },
        }),
        getDocument: build.query<DocumentInfo, UUID>({
            query: (documentId) => ({
                url: `documentos/${documentId}`,
            }),
            transformResponse: (response: Response<DocumentInfo>) => {
                return response.data;
            },
        }),
        patchDocument: build.mutation<any, { documentId: UUID; body: {} }>({
            query: ({ documentId, body }) => ({
                url: `documentos/${documentId}`,
                method: "PATCH",
                body: body,
            }),
            transformResponse: (response: Response<DocumentInfo>) => {
                return response.data;
            },
        }),
        deleteDocument: build.mutation<any, UUID>({
            query: (documentId) => ({
                url: `documentos/${documentId}`,
                method: "DELETE",
            }),
        }),
        postDocument: build.mutation<DocumentInfo, PayloadDocument>({
            query: (data: PayloadDocument) => ({
                url: "documentos",
                method: "POST",
                body: data,
            }),
            transformResponse: (response: Response<DocumentInfo>) => {
                return response.data;
            },
        }),
        postDocumentReasonReject: build.mutation<any, { documentId: UUID; body: unknown }>({
            query: ({ documentId, body }) => ({
                url: `documentos/${documentId}/razones_rechazo`,
                method: "POST",
                body,
            }),
        }),
        getUrlToDownloadDoc: build.mutation<string, string>({
            query: (fileKey) => ({
                url: `generar_url/descargar`,
                method: "POST",
                body: {
                    file_key: fileKey,
                },
            }),
            transformResponse: (response: Response<string>) => {
                return response.data;
            },
        }),
        getSignUrlToUploadDoc: build.query<Response<SignedUrlFile>, PayloadSignUrl>({
            query: (body) => ({
                url: `generar_url/subir`,
                method: "POST",
                body,
            }),
        }),
        getSignUrlToUploadContractDoc: build.query<Response<SignedUrlFile>, any>({
            query: (body) => ({
                url: `generar_url/documento_valor`,
                method: "POST",
                body,
            }),
        }),
        getSignUrlToUploadLogo: build.query<Response<SignedUrlFile>, any>({
            query: (body) => ({
                url: `generar_url/subir_logo`,
                method: "POST",
                body,
            }),
        }),
        postUploadFile: build.mutation<void, PayloadUploadFile>({
            query: (data: PayloadUploadFile) => ({
                url: data.url,
                method: "POST",
                body: data.body,
                formData: true,
            }),
        }),
        getContractDocs: build.query<Array<ContractualDoc>, string>({
            query: (params) => ({
                url: `documentos/documents_contract?${params}`,
            }),
            transformResponse: (response: ResponsePagination<ContractualDoc>) => {
                return response.data.data;
            },
        }),
        postContractDoc: build.mutation<ContractualDoc, unknown>({
            query: (data) => ({
                url: "documentos/documents_contract",
                method: "POST",
                body: data,
            }),
            transformResponse: (response: Response<ContractualDoc>) => {
                return response.data;
            },
        }),
        patchContractDoc: build.mutation<ContractualDoc, { contractDocId: UUID; body: any }>({
            query: ({ contractDocId, body }) => ({
                url: `documentos/documents_contract/${contractDocId}`,
                method: "PATCH",
                body: body,
            }),
            transformResponse: (response: Response<ContractualDoc>) => {
                return response.data;
            },
        }),
        deleteContractDoc: build.mutation<void, { contractDocId: UUID }>({
            query: ({ contractDocId }) => ({
                url: `documentos/documents_contract/${contractDocId}`,
                method: "DELETE",
            }),
        }),
        getContractDocsZipLink: build.query<string, { applicationId: UUID }>({
            query: ({ applicationId }) => ({
                url: `documentos/documents_contract/download_zip/${applicationId}`,
            }),
            transformResponse: (response: Response<string>) => {
                const tmp = JSON.parse(response.data);
                return tmp["data"];
            },
        }),
        postGenerateDoc: build.mutation<
            any,
            {
                applicationId: UUID;
                documentType: string;
                personId?: UUID;
                clientId?: UUID;
                guarantee_id?: UUID;
                company?: string;
            }
        >({
            query: ({ applicationId, documentType, personId, clientId, guarantee_id, company }) => ({
                url: `generar_documento`,
                method: "POST",
                body: {
                    loan_application_id: applicationId,
                    document_type: documentType,
                    person_id: personId,
                    client_id: clientId,
                    guarantee_id: guarantee_id,
                    company: company,
                },
            }),
        }),
        getInternalSignatories: build.query<Array<InternalSignatories>, string>({
            query: (params) => ({
                url: `documentos/internal_signatories?${params}`,
            }),
            transformResponse: (response: ResponsePagination<InternalSignatories>) => {
                return response.data.data;
            },
        }),
        postSendToSignContractualDocs: build.mutation<any, { loanApplicationId: UUID; releaseUserName: string }>({
            query: (payload) => ({
                url: `/signature_engine/contractual_docs`,
                method: "POST",
                body: {
                    loan_application_id: payload.loanApplicationId,
                    user_name: `BPM - ${payload.releaseUserName}`,
                },
            }),
            transformResponse: (response: any) => {
                return response.data;
            },
        }),
        getGenerateUnifiedDoc: build.query<void, { applicationId: UUID; code: string }>({
            query: ({ applicationId, code }) => ({
                url: `generar_documento/documento_unificado/${applicationId}/${code}`,
            }),
        }),
    }),
});

export const {
    useGetDocumentsByAppQuery,
    useLazyGetDocumentsByAppQuery,
    useGetFileDocumentsQuery,
    useLazyGetFileDocumentsQuery,
    useGetDocumentsQuery,
    useLazyGetDocumentQuery,
    useGetContractDocsQuery,
    useLazyGetContractDocsQuery,
    useLazyGetDocumentsQuery,
    usePatchDocumentMutation,
    useDeleteDocumentMutation,
    usePostDocumentReasonRejectMutation,
    useGetUrlToDownloadDocMutation,
    useLazyGetSignUrlToUploadDocQuery,
    usePostUploadFileMutation,
    usePostDocumentMutation,
    usePostContractDocMutation,
    usePatchContractDocMutation,
    useDeleteContractDocMutation,
    usePostGenerateDocMutation,

    usePostDocumentsZipLinkMutation,
    useLazyGetContractDocsZipLinkQuery,
    useLazyGetDocumentsV2Query,
    useLazyGetSignUrlToUploadContractDocQuery,
    useLazyGetInternalSignatoriesQuery,
    usePostSendToSignContractualDocsMutation,
    useLazyGetGenerateUnifiedDocQuery,
    useLazyGetSignUrlToUploadLogoQuery,
} = documentsApi;
